import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { DirectusService } from './directus.service';

const NEXT_PATH_KEY = 'officer_next_path';

/** Allowed path prefixes for post-login redirect. Prevents open redirect via next param. */
const SAFE_NEXT_PREFIXES = ['/dashboard', '/events', '/sales', '/projects', '/people', '/marketing', '/commerce', '/flows', '/website-content', '/analytics', '/members', '/membership-plans'];

function isSafeNextPath(path: string): boolean {
  const p = path.trim();
  if (p === '' || !p.startsWith('/') || p.startsWith('//')) return false;
  if (/javascript\s*:|data\s*:/i.test(p)) return false;
  return SAFE_NEXT_PREFIXES.some((prefix) => p === prefix || p.startsWith(prefix + '/'));
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  readonly user = signal<Record<string, unknown> | null>(null);

  constructor(
    private readonly directus: DirectusService,
    private readonly router: Router
  ) {}

  /** Returns a safe path for post-login redirect. Use when consuming next param from URL. */
  getSafeNextPath(raw: string): string {
    return isSafeNextPath(raw) ? raw.trim() : '/dashboard';
  }

  async refreshUser(): Promise<Record<string, unknown> | null> {
    if (!this.directus.token) {
      this.user.set(null);
      try {
        const sessionUser = await this.directus.me();
        this.user.set(sessionUser);
        return sessionUser;
      } catch {
        return null;
      }
    }

    try {
      const user = await this.directus.me();
      this.user.set(user);
      return user;
    } catch {
      this.directus.clearToken();
      try {
        const sessionUser = await this.directus.me();
        this.user.set(sessionUser);
        return sessionUser;
      } catch {
        this.user.set(null);
        return null;
      }
    }
  }

  async ensureAuthenticated(): Promise<boolean> {
    if (this.user()) return true;
    const user = await this.refreshUser();
    return Boolean(user);
  }

  async startSsoLogin(nextPath = '/dashboard'): Promise<void> {
    const safe = this.getSafeNextPath(nextPath);
    sessionStorage.setItem(NEXT_PATH_KEY, safe);

    const configured = this.directus.ssoProviderFromConfig();
    let provider = configured;

    if (!provider) {
      const providers = await this.directus.listAuthProviders();
      const auth0Match = providers.find((entry) => entry.name?.toLowerCase().includes('auth0'));
      provider = auth0Match?.name || providers[0]?.name || '';
    }

    if (!provider) throw new Error('No SSO provider found in Directus /auth/providers');

    const callbackUrl = `${window.location.origin}/auth/callback`;
    const loginUrl = new URL(this.directus.buildSsoLoginUrl(provider, callbackUrl));
    // Always use JSON mode: the Command Centre is on a different subdomain from the Directus API,
    // so session cookies set by Directus won't cross over. JSON mode returns an access_token via
    // the callback exchange that gets stored in localStorage and sent as a Bearer token.
    loginUrl.searchParams.set('mode', 'json');
    window.location.assign(loginUrl.toString());
  }

  async completeSsoLogin(accessToken?: string): Promise<void> {
    if (accessToken) {
      this.directus.setToken(accessToken);
    } else {
      // Session-mode SSO should rely on session cookies from callback.
      this.directus.clearToken();
    }
    const user = await this.refreshUser();
    if (!user) throw new Error('Unable to load user profile after SSO login');
  }

  consumeNextPath(): string {
    const nextPath = sessionStorage.getItem(NEXT_PATH_KEY) || '/dashboard';
    sessionStorage.removeItem(NEXT_PATH_KEY);
    return nextPath;
  }

  logout(): void {
    this.directus.clearToken();
    this.user.set(null);
    this.router.navigateByUrl('/login');
  }
}
